import type { ListboxProps } from '@headlessui/react';
import { Listbox } from '@headlessui/react';
import type { NewCustomer } from '@weft/types';
import type { Replace } from '@weft/utils';
import { isNotNullOrUndefined } from '@weft/utils';
import cn from 'classnames';
import type { FieldPath } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { ErrorLabel } from '../../../../components/design/ErrorLabel';
import { ArrowIcon } from '../../../../components/icons/ArrowIcon';
import { ARROW_ICON_DIRECTIONS } from '../../../../components/icons/ArrowIcon/types/arrow-icon-direction';

type ListBoxCustomerInput<Value> = Replace<ListboxProps<React.ElementType, Value, unknown>, 'name', FieldPath<NewCustomer>>;
export type CreateCustomerSettingsSelectProps<Value> = ListBoxCustomerInput<Value> & {
  title: string;
  items: Value[];
  parseItemToString?: (item: Value) => string;
};

export const CreateCustomerSettingsSelect = <Value, _ = undefined>({ title, items, parseItemToString, ...props }: CreateCustomerSettingsSelectProps<Value>) => {
  const { getFieldState } = useFormContext<NewCustomer>();
  const errorMessage = getFieldState(props.name).error?.message;
  const hasError = getFieldState(props.name)?.error?.message;

  return (
    <div className="flex flex-col">
      <Listbox
        {...props}
        as='div'
        className={cn(
          'relative h-16 w-96 rounded-2lg border bg-white laptop:w-72',
          hasError && 'border-input-error',
          !hasError && 'border-line',
        )}
      >
        {({ value }) => (
          <>
            <Listbox.Button className='h-full w-full cursor-pointer bg-transparent'>
              <div className="flex items-start justify-between p-5">
                <span className="text-filled-text">{title}</span>
                <span className='flex items-center space-x-2.5'>
                  <span className="min-h-6 text-style truncate w-32 text-right">{isNotNullOrUndefined(value) && (parseItemToString?.(value) ?? (typeof value === 'string' && value))}</span>
                  <ArrowIcon
                    className='text-style'
                    direction={ARROW_ICON_DIRECTIONS.DOWN}
                  />
                </span>
              </div>
            </Listbox.Button>
            <Listbox.Options className="absolute z-10 mt-1 w-full text-center">
              {items.map((item, index) => (
                <Listbox.Option
                  key={index}
                  value={item}
                  className="cursor-pointer"
                >
                  <div className="hover:bg-input bg-white py-2 shadow-sm" >{isNotNullOrUndefined(item) && (parseItemToString?.(item) ?? (typeof item === 'string' && item))}</div>
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </>
        )}
      </Listbox>

      {hasError && (
        <ErrorLabel
          errorMessage={errorMessage!}
        />
      )}
    </div>
  );
};